import React, { useState, useContext, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { ArtContext } from "../Contexts/ArtContext";
import { ArtistContext } from "../Contexts/ArtistContext";
import { CategoryContext } from "../Contexts/CategoryContext";
import { CollectionContext } from "../Contexts/CollectionContext";
import Alert from "../components/Alert";

const ArtForm = () => {
  const navigate = useNavigate();
  const { createArt, error } = useContext(ArtContext);
  const { artists, fetchArtists } = useContext(ArtistContext);
  const { category, fetchCategories } = useContext(CategoryContext);
  const { allCollections, fetchCollections } = useContext(CollectionContext);

  const [alert, setAlert] = useState({ status: "", message: "" });

  useEffect(() => {
    const fetchData = async () => {
      await fetchArtists();
      await fetchCategories();
      await fetchCollections();
    };

    fetchData();
  }, []);

  const [editions, setEditions] = useState([
    { editionType: "Original Art Work", price: "" },
  ]);
  const [artImageCover, setArtImageCover] = useState(null);
  const [artImages, setArtImages] = useState([]);
  const [artDescription, setArtDescription] = useState("");
  const [artName, setArtName] = useState("");
  const [productCode, setProductCode] = useState("");
  const [artMaterial, setArtMaterial] = useState("");
  const [artSize, setSize] = useState("");
  const [artQuantity, setArtQuantity] = useState("");
  const [artCategory, setArtCategory] = useState("");
  const [artCollection, setArtCollection] = useState("");
  const [artArtist, setArtArtist] = useState("");

  const handleImageCoverChange = (event) => {
    setArtImageCover(event.target.files[0]);
  };

  const handleArtImagesChange = (event) => {
    setArtImages(Array.from(event.target.files));
  };

  const handleAddEdition = () => {
    setEditions([...editions, { editionType: "Original Art Work", price: "" }]);
  };

  const handleRemoveEdition = (index) => {
    const newEditions = editions.filter((_, i) => i !== index);
    setEditions(newEditions);
  };

  const handleEditionChange = (index, event) => {
    const { name, value } = event.target;
    const newEditions = editions.map((edition, i) =>
      i === index ? { ...edition, [name]: value } : edition
    );
    setEditions(newEditions);
  };

  // Handle selecting a category
  const handleCategoryChange = (e) => {
    setArtCategory(e.target.value);
    if (e.target.value) {
      setArtCollection(""); // Clear collection when category is selected
    }
  };

  // Handle selecting a collection
  const handleCollectionChange = (e) => {
    setArtCollection(e.target.value);
    if (e.target.value) {
      setArtCategory(""); // Clear category when collection is selected
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Create a new FormData instance
    const formData = new FormData();

    // Append form fields to FormData
    formData.append("artName", artName);
    formData.append("productCode", productCode);
    formData.append("artDescription", artDescription);
    formData.append("artSize", artSize);
    formData.append("artMaterial", artMaterial);
    formData.append("quantity", artQuantity);
    formData.append("artists", artArtist);

    // Append single file
    if (artImageCover) {
      formData.append("artImageCover", artImageCover);
    }

    // Append multiple files
    artImages.forEach((image) => {
      formData.append(`artImages`, image);
    });

    // Append JSON arrays
    if (artCategory) {
      formData.append("categories", artCategory);
    }
    if (artCollection) {
      formData.append("collections", artCollection);
    }
    formData.append("editions", JSON.stringify(editions));

    // Log the FormData object
    // for (let pair of formData.entries()) {
    //   console.log(`${pair[0]}: ${pair[1]}`);
    // }

    try {
      await createArt(formData);
      // console.log("Art created successfully");
      setAlert({ status: "success", message: "Art created successfully!" });
      setTimeout(() => {
        navigate("/admin/view/art");
      }, 2000);
    } catch (error) {
      const errorMessage = error.message || "Failed to create art.";
      setAlert({ status: "error", message: errorMessage });
    }
  };

  const handleCancel = () => {
    navigate("/admin/dashboard");
  };

  return (
    <div className="bg-gray-100 py-10">
      {alert.message && (
        <Alert
          status={alert.status}
          message={alert.message}
          onClose={() => setAlert({ status: "", message: "" })}
        />
      )}
      <div className="max-w-3xl mx-auto bg-white p-8 rounded-lg shadow-lg">
        <h1 className="text-2xl font-bold mb-6 text-gray-800">
          Art Information
        </h1>
        <form onSubmit={handleSubmit}>
          {/* Art Name */}
          <div className="mb-4">
            <label
              htmlFor="artName"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Name
            </label>
            <input
              type="text"
              id="artName"
              name="artName"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artName}
              onChange={(e) => setArtName(e.target.value)}
              // required
            />
          </div>

          <div className="mb-4">
            <label
              htmlFor="productCode"
              className="block text-gray-700 font-medium mb-2"
            >
              Product Code
            </label>
            <input
              type="text"
              id="productCode"
              name="productCode"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={productCode}
              onChange={(e) => setArtName(e.target.value)}
              required
            />
          </div>

          {/* Art Description */}
          <div className="mb-4">
            <label
              htmlFor="artDescription"
              className="block text-gray-700 font-medium mb-2"
            >
              Description
            </label>
            <textarea
              id="artDescription"
              name="artDescription"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artDescription}
              onChange={(e) => setArtDescription(e.target.value)}
              required
            ></textarea>
          </div>

          {/* Art Size */}
          <div className="mb-4">
            <label
              htmlFor="artSize"
              className="block text-gray-700 font-medium mb-2"
            >
              Size
            </label>
            <input
              type="text"
              id="artSize"
              name="artSize"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artSize}
              onChange={(e) => setSize(e.target.value)}
              required
            />
          </div>

          {/* Art Material */}
          <div className="mb-4">
            <label
              htmlFor="artMaterial"
              className="block text-gray-700 font-medium mb-2"
            >
              Material
            </label>
            <input
              type="text"
              id="artMaterial"
              name="artMaterial"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artMaterial}
              onChange={(e) => setArtMaterial(e.target.value)}
              required
            />
          </div>

          {/* Art Quantity */}
          <div className="mb-4">
            <label
              htmlFor="artQuantity"
              className="block text-gray-700 font-medium mb-2"
            >
              Quantity
            </label>
            <input
              type="number"
              id="artQuantity"
              name="artQuantity"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artQuantity}
              onChange={(e) => setArtQuantity(e.target.value)}
              required
            />
          </div>

          {/* Art Cover Image */}
          <div className="mb-4">
            <label
              htmlFor="artImageCover"
              className="block text-gray-700 font-medium mb-2"
            >
              Cover Image
            </label>
            <input
              type="file"
              id="artImageCover"
              name="artImageCover"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              onChange={handleImageCoverChange}
              // required
            />
          </div>

          {/* Art Images */}
          <div className="mb-4">
            <label
              htmlFor="artImages"
              className="block text-gray-700 font-medium mb-2"
            >
              Art Images
            </label>
            <input
              type="file"
              id="artImages"
              name="artImages"
              multiple
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              onChange={handleArtImagesChange}
              // required
            />
          </div>

          {/* Artists Dropdown */}
          <div className="mb-4">
            <label
              htmlFor="artists"
              className="block text-gray-700 font-medium mb-2"
            >
              Artists
            </label>
            <select
              id="artists"
              name="artists"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artArtist}
              onChange={(e) => setArtArtist(e.target.value)}
              required
            >
              <option value="">Select an Artist</option>
              {artists.map((artist) => (
                <option key={artist._id} value={artist._id}>
                  {artist.name}
                </option>
              ))}
            </select>
          </div>

          {/* Collections Dropdown */}
          <div className="mb-4">
            <label
              htmlFor="collections"
              className="block text-gray-700 font-medium mb-2"
            >
              Collections
            </label>
            <select
              id="collections"
              name="collections"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artCollection}
              onChange={handleCollectionChange}
              required={!artCategory}
              disabled={!!artCategory}
            >
              <option value="">Select Collection</option>
              {allCollections.map((col) => (
                <option key={col._id} value={col._id}>
                  {col.name}
                </option>
              ))}
            </select>
          </div>

          {/* Category Dropdown */}
          <div className="mb-4">
            <label
              htmlFor="categories"
              className="block text-gray-700 font-medium mb-2"
            >
              Category
            </label>
            <select
              id="categories"
              name="categories"
              className="w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
              value={artCategory}
              onChange={handleCategoryChange}
              required={!artCollection}
              disabled={!!artCollection}
            >
              <option value="">Select a Category</option>
              {category.map((cat) => (
                <option key={cat._id} value={cat._id}>
                  {cat.name}
                </option>
              ))}
            </select>
          </div>

          {/* Editions */}
          <div className="mb-4">
            <label
              htmlFor="editions"
              className="block text-gray-700 font-medium mb-2"
            >
              Editions
            </label>
            {editions.map((edition, index) => (
              <div key={index} className="mb-4">
                <input
                  type="text"
                  name="editionType"
                  placeholder="Edition Type"
                  className="mr-2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={edition.editionType}
                  onChange={(e) => handleEditionChange(index, e)}
                  required
                />
                <input
                  type="text"
                  name="price"
                  placeholder="Price"
                  className="mr-2 px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-600"
                  value={edition.price}
                  onChange={(e) => handleEditionChange(index, e)}
                  required
                />
                {index > 0 && (
                  <button
                    type="button"
                    className="px-4 py-2 bg-red-600 text-white rounded-lg"
                    onClick={() => handleRemoveEdition(index)}
                  >
                    Remove
                  </button>
                )}
              </div>
            ))}
            <button
              type="button"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg"
              onClick={handleAddEdition}
            >
              Add Edition
            </button>
          </div>

          <div className="flex justify-end">
            <button
              type="button"
              className="mr-4 px-4 py-2 bg-gray-400 text-white rounded-lg"
              onClick={handleCancel}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-600 text-white rounded-lg"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ArtForm;
